import React, { Suspense, lazy } from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import './global.css';
import App from './App';
import reportWebVitals from './reportWebVitals';

// Dynamic import Sentry modules only when needed
const initSentry = async () => {
  if (process.env.NODE_ENV === 'production') {
    console.log('Lazy loading Sentry in production mode');
    try {
      const Sentry = await import("@sentry/react");
      const { browserTracingIntegration } = await import("@sentry/browser");
      const { Replay } = await import("@sentry/replay");
      
      Sentry.init({
        dsn: "https://941a62970d9b71f2df1d0761478a8428@o4509152042287104.ingest.us.sentry.io/4509152217923584",
        integrations: [
          browserTracingIntegration({
            tracePropagationTargets: ["localhost", "pipersofbelgrade.netlify.app"],
          }),
          new Replay(),
        ],
        // Performance Monitoring
        tracesSampleRate: 1.0,
        // Session Replay
        replaysSessionSampleRate: 0.1,
        replaysOnErrorSampleRate: 1.0,
        // Suppress NotAllowedError and similar permission-denied errors
        beforeSend(event, hint) {
          const error = hint && hint.originalException;
          if (error && error.name === "NotAllowedError") {
            return null;
          }
          if (error && error.message && (
            error.message.includes("Microphone access denied") ||
            error.message.toLowerCase().includes("permission denied")
          )) {
            return null;
          }
          return event;
        }
      });
      
      console.log('Sentry initialized successfully');
      return true;
    } catch (error) {
      console.error('Failed to initialize Sentry:', error);
      return false;
    }
  }
  return false;
};

// Lazy load the SentryErrorBoundary component
const SentryErrorBoundary = lazy(() => 
  import('./components/common/SentryErrorBoundary').then(module => ({
    default: module.SentryErrorBoundary
  }))
);

// Initialize Sentry in production as soon as possible but don't block rendering
if (process.env.NODE_ENV === 'production') {
  initSentry();
}

// Check for missing REACT_APP_MY_TEST_API_KEY
if (!process.env.REACT_APP_MY_TEST_API_KEY) {
  alert('REACT_APP_MY_TEST_API_KEY is missing. This should be in your .env.local file on development machines or defined in an environment variable in production systems such as Netlify.');
}

// Simple fallback for lazy loaded components
const LoadingFallback = () => <div>Loading...</div>;

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    {process.env.NODE_ENV === 'production' ? (
      <Suspense fallback={<LoadingFallback />}>
        <SentryErrorBoundary>
          <App />
        </SentryErrorBoundary>
      </Suspense>
    ) : (
      <App />
    )}
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
